import React, { useEffect, useState } from 'react'
import { FiArrowLeft, FiEdit2, FiTrash2, FiShoppingCart } from 'react-icons/fi'
import { AccountBalance, Business, AccountBalanceWallet } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  Button,
  Avatar,
  Chip,
  Switch,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  TablePagination,
} from '@mui/material'
import {
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalHospital as EmergencyIcon,
} from '@mui/icons-material'
import api from '@/services/api'
import { useParams } from 'react-router-dom'

export default function AgencyPayoutSettings(): JSX.Element {
  const navigate = useNavigate()
  const { id } = useParams()
  const [payoutType, setPayoutType] = useState<'Percentage' | 'Fixed Amount'>('Percentage')
  const [providerShare, setProviderShare] = useState('70')
  const [agencyShare, setAgencyShare] = useState('26')
  const [platformFee, setPlatformFee] = useState('4')
  const [payoutData, setPayoutData] = useState<any[]>([])
  const [payoutStatus,setPayoutStatus] = useState<any>({})

  // Pagination state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const customPayoutRules = payoutData
    ? payoutData.filter((item: any) => !item.is_default && item.package_id)
    : []

  const paginatedRules = customPayoutRules.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= customPayoutRules.length) {
      setPage(Math.max(0, Math.ceil(customPayoutRules.length / rowsPerPage) - 1))
    }
  }, [customPayoutRules.length, page, rowsPerPage])

  // Snackbar notification state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success'
  })

  const showMessage = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }

  const fecthPayoutData = async () => {
    try {
      const res = await api.get(`/payout-setting/payout/${id}`)
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setPayoutData(res.data.data);
        setPayoutStatus(res?.data?.payoutStats)
        
        // Find default payout setting
        const defaultPayout = res.data.data.find((item: any) => item.is_default || !item.package_id)
        if (defaultPayout) {
          setPayoutType(defaultPayout.payout_type === 'percentage' ? 'Percentage' : 'Fixed Amount')
          setProviderShare(String(defaultPayout.provider_share))
          setAgencyShare(String(defaultPayout.agency_share))
          setPlatformFee(String(defaultPayout.platform_fee || '4'))
        }
      }
    } catch (err) {
      console.error('Error fetching payout data:', err)
    }
  }

  const [therapistInfo, setTherapistInfo] = useState<any>(null)

  const fetchTherapistInfo = async () => {
    try {
      const res = await api.get(`/therapist/get-therapist/${id}`)
      if (res.data && res.data.data) {
        setTherapistInfo(res.data.data)
      }
    } catch (err) {
      console.error('Error fetching therapist info:', err)
    }
  }

  useEffect(() => {
    if (id) {
      fecthPayoutData()
      fetchTherapistInfo()
      fetchPackages()
    }
  }, [id])

  const basePayment = 150
  const numericPlatformFee = parseFloat(platformFee) || 4
  const numericAgencyShare = parseFloat(agencyShare) || 0
  const numericProviderShare = parseFloat(providerShare) || 0

  const calculatedPlatformFee = payoutType === 'Percentage'
    ? (basePayment * numericPlatformFee) / 100
    : (basePayment * 0.04)

  const calculatedAgencyShare = payoutType === 'Percentage'
    ? (basePayment * numericAgencyShare) / 100
    : numericAgencyShare

  const calculatedProviderShare = payoutType === 'Percentage'
    ? (basePayment * numericProviderShare) / 100
    : numericProviderShare

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false)
  const [packages, setPackages] = useState<any[]>([])
  const [selectedPackage, setSelectedPackage] = useState('')
  const [dialogPayoutType, setDialogPayoutType] = useState<'Percentage' | 'Fixed Amount'>('Percentage')
  const [dialogProviderShare, setDialogProviderShare] = useState('0')
  const [dialogAgencyShare, setDialogAgencyShare] = useState('0')
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)

  const fetchPackages = async () => {
    try {
      const response = await api.get('/services/agency-therapist-package', {
        params: { therapist_id: id }
      })
      let packagesList = []
      if (response.data) {
        if (Array.isArray(response.data)) {
          packagesList = response.data
        } else if (Array.isArray(response.data.data)) {
          packagesList = response.data.data
        } else if (Array.isArray(response.data.services)) {
          packagesList = response.data.services
        }
      }
      setPackages(packagesList)
    } catch (err) {
      console.error('Error fetching packages:', err)
    }
  }

  useEffect(() => {
    if (id && openDialog) {
      fetchPackages()
    }
  }, [id, openDialog])

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingRuleId(null)
    setSelectedPackage('')
    setDialogProviderShare('0')
    setDialogAgencyShare('0')
  }

  const handleSaveRule = async () => {
    if (!selectedPackage) {
      showMessage('Please select a service package', 'warning')
      return
    }

    const provShareNum = parseFloat(dialogProviderShare) || 0
    const agShareNum = parseFloat(dialogAgencyShare) || 0
    const platFeeNum = parseFloat(platformFee) || 0

    if (dialogPayoutType === 'Percentage') {
      const total = provShareNum + agShareNum + platFeeNum
      if (Math.abs(total - 100) > 0.01) {
        showMessage(`The sum of Provider Share (${provShareNum}%), Agency Share (${agShareNum}%), and Platform Fee (${platFeeNum}%) must equal 100%. Currently it is ${total}%.`, 'warning')
        return
      }
    }

    try {
      const payload = {
        provider_id: id,
        package_id: selectedPackage,
        payout_type: dialogPayoutType === 'Percentage' ? 'percentage' : 'fixed',
        provider_share: provShareNum,
        agency_share: agShareNum,
        platform_fee: platFeeNum,
        is_default: false,
        status: true
      }
      let res
      if (editingRuleId) {
        res = await api.put(`/payout-setting/payout/${editingRuleId}/package`, payload)
      } else {
        res = await api.post('/payout-setting/payout/package', payload)
      }
      if (res.data.success) {
        fecthPayoutData()
        handleCloseDialog()
        showMessage(res.data.message, 'success')
      }else{
        showMessage(res.data.message, 'error')
      }
    } catch (err:any) {
      console.error('Error saving custom payout rule:', err)
      showMessage(err?.response?.data?.message || 'Failed to save custom payout rule.', 'error')
    }
  }

  const handleEditClick = (item: any) => {
    setEditingRuleId(item._id)
    const pkgId = typeof item.package_id === 'object' ? item.package_id._id : item.package_id
    setSelectedPackage(pkgId || '')
    setDialogPayoutType(item.payout_type === 'percentage' ? 'Percentage' : 'Fixed Amount')
    setDialogProviderShare(String(item.provider_share))
    setDialogAgencyShare(String(item.agency_share))
    setOpenDialog(true)
  }

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)

  const handleDeleteClick = (ruleId: string) => {
    setDeleteTargetId(ruleId)
    setOpenDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return
    try {
      await api.delete(`/payout-setting/payout/${deleteTargetId}/package`)
      fecthPayoutData()
      setOpenDeleteConfirm(false)
      setDeleteTargetId(null)
    } catch (err) {
      console.error('Error deleting custom payout rule:', err)
    }
  }

  const handleToggleStatus = async (item: any) => {
    try {
      const res = await api.patch(`/payout-setting/payout/${item._id}/status`, {
        status: !item.status
      })
      if (res.data && res.data.success) {
        showMessage(res.data.message || 'Status updated successfully.', 'success')
      } else {
        showMessage('Status updated successfully.', 'success')
      }
      fecthPayoutData()
    } catch (err: any) {
      console.error('Error toggling status:', err)
      const errMsg = err?.response?.data?.message || 'Failed to update status.'
      showMessage(errMsg, 'error')
    }
  }

  const handleNumberChange = (val: string, setter: (v: string) => void, isDefault: boolean, field: 'provider' | 'agency') => {
    const numericVal = val.replace(/[^0-9.]/g, '')
    const parts = numericVal.split('.')
    const cleanedVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : numericVal
    
    setter(cleanedVal)

    if (isDefault) {
      if (payoutType === 'Percentage') {
        const parsedVal = parseFloat(cleanedVal) || 0
        const fee = parseFloat(platformFee) || 0
        if (field === 'provider') {
          const calculatedAgency = Math.max(0, 100 - parsedVal - fee)
          setAgencyShare(String(Number(calculatedAgency.toFixed(2))))
        } else {
          const calculatedProvider = Math.max(0, 100 - parsedVal - fee)
          setProviderShare(String(Number(calculatedProvider.toFixed(2))))
        }
      }
    } else {
      if (dialogPayoutType === 'Percentage') {
        const parsedVal = parseFloat(cleanedVal) || 0
        const fee = parseFloat(platformFee) || 0
        if (field === 'provider') {
          const calculatedAgency = Math.max(0, 100 - parsedVal - fee)
          setDialogAgencyShare(String(Number(calculatedAgency.toFixed(2))))
        } else {
          const calculatedProvider = Math.max(0, 100 - parsedVal - fee)
          setDialogProviderShare(String(Number(calculatedProvider.toFixed(2))))
        }
      }
    }
  }

  const defaultTotal = (parseFloat(providerShare) || 0) + (parseFloat(agencyShare) || 0) + (parseFloat(platformFee) || 0)
  const isDefaultOver100 = payoutType === 'Percentage' && defaultTotal > 100

  const dialogTotal = (parseFloat(dialogProviderShare) || 0) + (parseFloat(dialogAgencyShare) || 0) + (parseFloat(platformFee) || 0)
  const isDialogOpenOver100 = dialogPayoutType === 'Percentage' && dialogTotal > 100
  const handleSaveDefaultPayout = async () => {
    const provShareNum = parseFloat(providerShare) || 0
    const agShareNum = parseFloat(agencyShare) || 0
    const platFeeNum = parseFloat(platformFee) || 0

    if (payoutType === 'Percentage') {
      const total = provShareNum + agShareNum + platFeeNum
      if (Math.abs(total - 100) > 0.01) {
        showMessage(`The sum of Provider Share (${provShareNum}%), Agency Share (${agShareNum}%), and Platform Fee (${platFeeNum}%) must equal 100%. Currently it is ${total}%.`, 'warning')
        return
      }
    }

    try {
      const payload = {
        payout_type: payoutType === 'Percentage' ? 'percentage' : 'fixed',
        provider_share: provShareNum,
        agency_share: agShareNum,
        platform_fee: platFeeNum,
        provider_id: id
      }
      const res = await api.post('/payout-setting/payout/default', payload)
      if (res.data) {
        showMessage('Default payout settings saved successfully.', 'success')
        fecthPayoutData()
      }
    } catch (err) {
      console.error('Error saving default payout settings:', err)
      showMessage('Failed to save default payout settings.', 'error')
    }
  }

  const handleResetDefaultPayout = () => {
    setProviderShare("0")
    setAgencyShare("0")
    // setPlatformFee("0")
    // fecthPayoutData()
  }

  return (
    <div className="min-h-screen bg-[#F7F9FB] p-6 lg:p-10 font-sans text-slate-800">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-[#5C5C77] hover:text-slate-800 font-semibold mb-6 transition-colors text-sm"
        >
          <FiArrowLeft className="mr-2" size={18} /> Back to Providers
        </button>

        {/* Profile Card */}
        <Card className="rounded-[20px] shadow-sm border border-slate-100 mb-6 bg-white" elevation={0}>
          <div className="p-5 md:p-7 flex flex-col md:flex-row justify-between items-center md:items-center gap-6">
            <div className="flex items-center gap-5">
              <div className="relative">
                <Avatar
                  src={therapistInfo?.profilePhotoUrl || "https://i.pravatar.cc/150?img=47"}
                  alt={therapistInfo ? `${therapistInfo.firstName} ${therapistInfo.lastName}` : "Provider Photo"}
                  sx={{ width: 72, height: 72, borderRadius: 1 }}
                />
                <div className="absolute bottom-1 right-1 w-3.5 h-3.5 bg-[#22C55E] rounded-full border-2 border-white"></div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-lg font-bold text-[#3A3A4D]">
                    {therapistInfo ? `${therapistInfo.firstName} ${therapistInfo.lastName}` : "Loading..."}
                  </h2>
                  {therapistInfo?.license_type && (
                    <Chip label={therapistInfo.license_type} size="small" sx={{ bgcolor: '#4648D41A', color: '#7A6FA1', fontWeight: 700, fontSize: '0.65rem', height: '20px' }} />
                  )}
                </div>
                <div className="text-[#6B7280] text-sm flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <EmailIcon sx={{ color: '#464554', fontSize: 18 }} />
                    {therapistInfo?.email || "..."}
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon sx={{ color: '#464554', fontSize: 18 }} />
                    {therapistInfo?.phone || "..."}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-8 md:gap-14 text-center">
              <div>
                <div className="text-[10px] font-bold text-[#8A8A9E] tracking-widest uppercase mb-2">Total Packages</div>
                <div className="text-2xl font-bold text-[#7160A5]">{payoutStatus?.totalPackage}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-[#8A8A9E] tracking-widest uppercase mb-2">Active Patients</div>
                <div className="text-2xl font-bold text-[#7160A5]">
                  {payoutStatus?.activePatient}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-[#8A8A9E] tracking-widest uppercase mb-2">Total Earning</div>
                <div className="text-2xl font-bold text-[#4B5563]">
                  {payoutStatus?.totalEarning}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <div className="lg:col-span-2 space-y-6">
            {/* Default Visit Payout */}
            <Card className="rounded-[20px] shadow-sm border border-slate-100 bg-white" elevation={0}>
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-[#574F75] mb-1">Default Visit Payout</h3>
                    <p className="text-sm text-[#8A8A9E]">Standard configuration for all standard clinical visits.</p>
                  </div>
                  <div className="bg-[#F3F4F6] p-1 rounded-full flex">
                    <button
                      className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${payoutType === 'Percentage' ? 'bg-[#7663A6] text-white shadow-sm' : 'text-[#6B7280] hover:text-slate-700'}`}
                      // onClick={() => setPayoutType('Percentage')}
                    >
                      Percentage
                    </button>
                    {/* <button
                      className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${payoutType === 'Fixed Amount' ? 'bg-[#7663A6] text-white shadow-sm' : 'text-[#6B7280] hover:text-slate-700'}`}
                      onClick={() => setPayoutType('Fixed Amount')}
                    >
                      Fixed Amount
                    </button> */}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-[#6B7280] mb-2">Provider Share</label>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={providerShare}
                      onChange={(e) => handleNumberChange(e.target.value, setProviderShare, true, 'provider')}
                      error={isDefaultOver100}
                      helperText={isDefaultOver100 ? "Total share cannot exceed 100%" : ""}
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{ typography: 'h6', color: 'text.secondary' }}>{payoutType === 'Percentage' ? '%' : '$'}</InputAdornment>,
                        sx: { borderRadius: '12px', bgcolor: 'white', '& fieldset': { borderColor: '#E5E7EB' }, '&.Mui-focused fieldset': { borderColor: '#7663A6' }, input: { fontSize: '1.25rem', fontWeight: 600, color: '#374151' } }
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#6B7280] mb-2">Agency Share</label>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={agencyShare}
                      onChange={(e) => handleNumberChange(e.target.value, setAgencyShare, true, 'agency')}
                      error={isDefaultOver100}
                      helperText={isDefaultOver100 ? "Total share cannot exceed 100%" : ""}
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{ typography: 'h6', color: 'text.secondary' }}>{payoutType === 'Percentage' ? '%' : '$'}</InputAdornment>,
                        sx: { borderRadius: '12px', bgcolor: 'white', '& fieldset': { borderColor: '#E5E7EB' }, '&.Mui-focused fieldset': { borderColor: '#7663A6' }, input: { fontSize: '1.25rem', fontWeight: 600, color: '#374151' } }
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#6B7280] mb-2">Platform Fee (Locked)</label>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={platformFee}
                      disabled
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{ typography: 'h6', color: 'text.secondary' }}>%</InputAdornment>,
                        sx: { borderRadius: '12px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB', borderStyle: 'dashed' }, input: { fontSize: '1.25rem', fontWeight: 600, color: '#9CA3AF' } }
                      }}
                    />
                  </div>
                </div>

                <Divider sx={{ borderColor: '#F3F4F6', mb: 4 }} />

                <div className="flex justify-end items-center gap-6">
                  <button 
                    onClick={handleResetDefaultPayout}
                    className="text-[#6B7280] font-semibold text-sm hover:text-slate-800 transition-colors cursor-pointer"
                  >
                    Reset
                  </button>
                  <Button
                    onClick={handleSaveDefaultPayout}
                    variant="contained"
                    disabled={isDefaultOver100}
                    sx={{
                      bgcolor: '#7663A6',
                      '&:hover': { bgcolor: '#645492' },
                      borderRadius: '24px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.2,
                      boxShadow: 'none',
                      fontSize: '0.95rem'
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </Card>

            {/* Package-wise Configuration */}
            <Card className="rounded-[20px] shadow-sm border border-slate-100 overflow-hidden bg-white" elevation={0}>
              <div className="p-6 md:p-8 flex flex-col md:flex-row justify-between items-start md:items-center">
                <div>
                  <h3 className="text-xl font-bold text-[#574F75] mb-1">Package-wise Configuration</h3>
                  <p className="text-sm text-[#8A8A9E]">Override default rates for specific service types.</p>
                </div>
                <Button
                  variant="text"
                  sx={{
                    color: '#6A5BC2',
                    fontWeight: 600,
                    textTransform: 'none',
                    bgcolor: '#F3F1F9',
                    borderRadius: '24px',
                    px: 3,
                    py: 1,
                    mt: { xs: 2, md: 0 },
                    '&:hover': { bgcolor: '#E8E5F4' },
                    fontSize: '0.85rem'
                  }}
                  startIcon={<span className="text-lg leading-none">+</span>}
                  onClick={() => {
                    setDialogPayoutType(payoutType)
                    setOpenDialog(true)
                  }}
                >
                  Add Custom Payout Rule
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="bg-[#F8FAFC] text-[10px] uppercase font-bold tracking-wider text-[#6B7280] border-y border-slate-100">
                    <tr>
                      <th className="px-8 py-5">Service Package</th>
                      <th className="px-6 py-5">Provider</th>
                      <th className="px-6 py-5">Agency</th>
                      <th className="px-6 py-5">Status</th>
                      <th className="px-6 py-5">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedRules.length > 0 ? (
                      paginatedRules.map((item: any) => (
                        <tr key={item._id} className="hover:bg-slate-50/50 transition-colors group">
                          <td className="px-8 py-6 font-bold text-[#3A3A4D]">
                            {typeof item.package_id === 'object' ? (item.package_id.packageName || item.package_id.name || 'Unnamed Package') : 'Custom Rule'}
                          </td>
                          <td className="px-6 py-6 font-bold text-[#7663A6]">
                            {item.provider_share}{item.payout_type === 'percentage' ? '%' : ''}
                          </td>
                          <td className="px-6 py-6 text-[#6B7280] font-medium">
                            {item.agency_share}{item.payout_type === 'percentage' ? '%' : ''}
                          </td>
                          <td className="px-6 py-6">
                            <Chip 
                              label={item.status ? "ACTIVE" : "INACTIVE"} 
                              size="small" 
                              sx={{ 
                                bgcolor: item.status ? '#DCFCE7' : '#F3F4F6', 
                                color: item.status ? '#16A34A' : '#6B7280', 
                                fontWeight: 800, 
                                fontSize: '0.65rem', 
                                letterSpacing: '0.5px' 
                              }} 
                            />
                          </td>
                          <td className="px-6 py-6 flex items-center gap-3">
                            <IconButton onClick={() => handleEditClick(item)} size="small" sx={{ color: '#9CA3AF', '&:hover': { color: '#6B7280' } }}><FiEdit2 size={16} /></IconButton>
                            <Switch 
                              size="small" 
                              checked={item.status} 
                              onChange={() => handleToggleStatus(item)}
                              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#7663A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#7663A6' } }} 
                            />
                            <IconButton onClick={() => handleDeleteClick(item._id)} size="small" sx={{ color: '#EF4444', opacity: 0.8, '&:hover': { opacity: 1 } }}><FiTrash2 size={16} /></IconButton>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-8 py-6 text-center text-sm text-[#8A8A9E]">
                          No custom payout rules configured.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {customPayoutRules.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={customPayoutRules.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid #F1F5F9',
                    '.MuiTablePagination-toolbar': {
                      px: 3,
                      py: 1.5,
                      color: '#4B5563',
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: '#6B7280',
                    },
                    '.MuiTablePagination-select': {
                      borderRadius: '8px',
                      '&:focus': {
                        borderRadius: '8px',
                      }
                    }
                  }}
                />
              )}
            </Card>

          </div>

          <div className="lg:col-span-1">
            {/* Distribution Flow */}
            <Card className="rounded-[20px] shadow-sm border border-slate-100 p-6 md:p-8 bg-white h-full" elevation={0}>
              <h3 className="text-xl font-bold text-[#574F75] mb-8">Distribution Flow</h3>

              <div className="relative pl-2">
                {/* Timeline line */}
                <div className="absolute left-[23px] top-4 bottom-12 w-[1.5px] bg-[#E5E7EB]"></div>

                <div className="space-y-6">
                  {/* Step 1 */}
                  <div className="relative flex items-center gap-4">
                    <div className="w-[45px] h-[45px] rounded-full bg-[#E5E7EB] flex items-center justify-center z-10 shrink-0 border-[4px] border-white">
                      <FiShoppingCart size={15} className="text-[#7A6FA1]" />
                    </div>
                    <div className="bg-[#F3F4F5] rounded-2xl p-3.5 flex-1">
                      <div className="text-[10px] font-bold text-[#464554] uppercase tracking-wider mb-1">Patient Payment</div>
                      <div className="text-[#574F75] font-bold">{payoutStatus?.patientPayment!=='N/A' ? `₹${payoutStatus?.patientPayment}` : 'N/A'}</div>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="relative flex items-center gap-4">
                    <div className="w-[45px] h-[45px] rounded-full bg-[#E5E7EB] flex items-center justify-center z-10 shrink-0 border-[4px] border-white">
                      <AccountBalance sx={{ color: '#7A6FA1', fontSize: 16 }} />
                    </div>
                    <div className="bg-[#F3F4F5] rounded-2xl p-3.5 flex-1">
                      <div className="text-[10px] font-bold text-[#464554] uppercase tracking-wider mb-1">Stripe & Platform </div>
                      <div className="text-[#574F75] font-bold">{payoutStatus?.stripePlatform!=='N/A' ? `$${payoutStatus?.stripePlatform}` : 'N/A'}</div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="relative flex items-center gap-4">
                    <div className="w-[45px] h-[45px] rounded-full bg-[#E5E7EB] flex items-center justify-center z-10 shrink-0 border-[4px] border-white">
                      <Business sx={{ color: '#7A6FA1', fontSize: 18 }} />
                    </div>
                    <div className="bg-[#F3F4F5] rounded-2xl p-3.5 flex-1">
                      <div className="text-[10px] font-bold text-[#464554] uppercase tracking-wider mb-1">Agency Share </div>
                      <div className="text-[#574F75] font-bold">{payoutStatus?.agencyShare!=='N/A' ? `$${payoutStatus?.agencyShare}` : 'N/A'}</div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="relative flex items-center gap-4">
                    <div className="w-[45px] h-[45px] rounded-full bg-[#E1E0FF] flex items-center justify-center z-10 shrink-0 border-[4px] border-white">
                      <AccountBalanceWallet sx={{ color: '#7A6FA1', fontSize: 16 }} />
                    </div>
                    <div className="bg-[#E1E0FF4D] rounded-2xl p-3.5 flex-1 border border-[#EBE9F4]">
                      <div className="text-[10px] font-bold text-[#7A6FA1] uppercase tracking-wider mb-1">Provider Payout </div>
                      <div className="text-[#7A6FA1] font-bold text-[17px]">{payoutStatus?.providerPayout!=='N/A' ? `$${payoutStatus?.providerPayout}` : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

        </div>
      </div>

      {/* Add Custom Payout Rule Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 3,
            boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        <div className="flex justify-between items-center mb-1">
          <DialogTitle sx={{ p: 0, fontWeight: 700, fontSize: '1.25rem', color: '#1E1E2F' }}>
            {editingRuleId ? 'Edit Custom Payout Rule' : 'Add Custom Payout Rule'}
          </DialogTitle>
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: '#9CA3AF' }}>
            <CloseIcon />
          </IconButton>
        </div>
        <p className="text-sm text-[#8A8A9E] mb-6">
          Configure custom payout settings for a specific service package.
        </p>

        <DialogContent sx={{ p: 0, overflowY: 'visible' }}>
          <div className="space-y-6">
            {/* Service Package */}
            <div>
              <label className="block text-sm font-medium text-[#6B7280] mb-2">Service Package</label>
              <FormControl fullWidth variant="outlined">
                <Select
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: '12px',
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7663A6' }
                  }}
                >
                  <MenuItem value="" disabled>
                    <span className="text-[#9CA3AF]">Select a package</span>
                  </MenuItem>
                  {packages.map((pkg) => (
                    <MenuItem key={pkg._id} value={pkg._id}>
                      {pkg.name || pkg.packageName || pkg.serviceTitle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Payout Type */}
            <div className="bg-gradient-to-r from-[#F5F3FF] to-[#EDE9FE] rounded-xl p-4 border border-[#DDD6FE] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#7663A6] flex items-center justify-center text-white shadow-sm shadow-[#7663A6]/20">
                  {dialogPayoutType === 'Percentage' ? (
                    <span className="text-lg font-bold">%</span>
                  ) : (
                    <span className="text-lg font-bold">$</span>
                  )}
                </div>
                <div>
                  <span className="block text-xs font-semibold text-[#7663A6] uppercase tracking-wider">
                    Payout Type
                  </span>
                  <span className="block text-base font-bold text-[#1E1E2F]">
                    {dialogPayoutType === 'Percentage' ? 'Percentage' : 'Fixed Amount Rate'}
                  </span>
                </div>
              </div>
            </div>

            {/* Distribution Inputs Box */}
            <div className="border border-slate-100 rounded-2xl p-5 bg-[#F9FAFB]/50 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">
                    Provider Receives ({dialogPayoutType === 'Percentage' ? '%' : '$'})
                  </label>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={dialogProviderShare}
                    onChange={(e) => handleNumberChange(e.target.value, setDialogProviderShare, false, 'provider')}
                    error={isDialogOpenOver100}
                    helperText={isDialogOpenOver100 ? "Total share cannot exceed 100%" : ""}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{dialogPayoutType === 'Percentage' ? '%' : '$'}</InputAdornment>,
                      sx: { 
                        borderRadius: '12px', 
                        bgcolor: 'white', 
                        '& fieldset': { borderColor: '#E5E7EB' }, 
                        input: { fontWeight: 600, color: '#374151', py: 1.5 } 
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">
                    Agency Receives ({dialogPayoutType === 'Percentage' ? '%' : '$'})
                  </label>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={dialogAgencyShare}
                    onChange={(e) => handleNumberChange(e.target.value, setDialogAgencyShare, false, 'agency')}
                    error={isDialogOpenOver100}
                    helperText={isDialogOpenOver100 ? "Total share cannot exceed 100%" : ""}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{dialogPayoutType === 'Percentage' ? '%' : '$'}</InputAdornment>,
                      sx: { 
                        borderRadius: '12px', 
                        bgcolor: 'white', 
                        '& fieldset': { borderColor: '#E5E7EB' }, 
                        input: { fontWeight: 600, color: '#374151', py: 1.5 } 
                      }
                    }}
                  />
                </div>
              </div>

              {dialogPayoutType === 'Percentage' && (() => {
                const isInitialState = (parseFloat(dialogProviderShare) || 0) === 0 && (parseFloat(dialogAgencyShare) || 0) === 0;
                const isError = !isInitialState && Math.abs((parseFloat(dialogProviderShare) || 0) + (parseFloat(dialogAgencyShare) || 0) + (parseFloat(platformFee) || 0) - 100) > 0.01;
                
                return (
                  <div className={`text-xs font-semibold px-1 ${isInitialState ? 'text-[#6B7280]' : isError ? 'text-[#D32F2F]' : 'text-emerald-600'}`}>
                    {isInitialState ? (
                      <span>Specify the distribution shares. Total must equal 100% (including the {platformFee}% platform fee).</span>
                    ) : isError ? (
                      <span>⚠️ Total Share is {((parseFloat(dialogProviderShare) || 0) + (parseFloat(dialogAgencyShare) || 0) + (parseFloat(platformFee) || 0)).toFixed(1)}%. It must equal 100% (Provider + Agency + Platform).</span>
                    ) : (
                      <span>✓ Total Share is 100% (Provider: {dialogProviderShare || 0}% + Agency: {dialogAgencyShare || 0}% + Platform: {platformFee || 0}%)</span>
                    )}
                  </div>
                );
              })()}

              {/* Platform Fee Row */}
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm font-medium text-[#4B5563]">Platform Fee</span>
                <span className="bg-[#4648D41A] text-[#7A6FA1] text-xs font-bold px-2.5 py-1 rounded-full">
                    {platformFee}%
                </span>
              </div>

              {/* Info Note */}
              <div className="flex gap-2.5 text-xs text-[#7A6FA1] bg-[#4648D40D] p-3.5 rounded-xl border border-[#4648D41A]">
                <InfoIcon sx={{ fontSize: 16, color: '#7A6FA1', mt: 0.1 }} />
                <span>Platform fee is automatically deducted before payout distribution.</span>
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ p: 0, mt: 4, gap: 2, justifyContent: 'center' }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderColor: '#E5E7EB',
              color: '#6B7280',
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.2,
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRule}
            variant="contained"
            disabled={isDialogOpenOver100}
            sx={{
              bgcolor: '#7663A6',
              '&:hover': { bgcolor: '#645492' },
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.2,
              boxShadow: 'none'
            }}
          >
            {editingRuleId ? 'Update Payout Rule' : 'Save Payout Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: 2.5,
            boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        <DialogTitle sx={{ p: 0, fontWeight: 700, fontSize: '1.2rem', color: '#1E1E2F', mb: 1 }}>
          Delete Custom Payout Rule
        </DialogTitle>
        <DialogContent sx={{ p: 0, mb: 3 }}>
          <p className="text-sm text-[#6B7280]">
            Are you sure you want to delete this custom payout rule? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ p: 0, gap: 1.5 }}>
          <Button
            onClick={() => setOpenDeleteConfirm(false)}
            variant="outlined"
            sx={{
              borderColor: '#E5E7EB',
              color: '#6B7280',
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              py: 1,
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: '#EF4444',
              '&:hover': { bgcolor: '#DC2626' },
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              py: 1,
              boxShadow: 'none'
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Beautiful toast notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: '12px',
            fontFamily: 'inherit',
            fontWeight: 600,
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
            bgcolor: snackbar.severity === 'success' ? '#7663A6' : undefined, // matching theme color for success
            '& .MuiAlert-icon': {
              color: '#fff'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}
